/**
 * vector-quote-form.js
 * Handles the Vectorize Quote product page:
 *  - Dynamic "Add Design" blocks (file upload + description per design)
 *  - "Get Custom Quote" button opens a popup
 *  - Popup collects name/email (auto-filled & hidden if logged in)
 *  - POSTs all design data as JSON to Zapier webhook
 */

(function () {
  'use strict';

  var CFG = window.VEC_CONFIG || {};
  var SUPABASE_URL    = CFG.supabaseUrl || '';
  var SUPABASE_KEY    = CFG.supabaseKey || '';
  var SUPABASE_BUCKET = 'File Uploads';
  var WEBHOOK_URL     = CFG.webhookUrl || '';
  var PRODUCT_TITLE   = CFG.productTitle || 'Vectorize Quote';
  var CUSTOMER_LOGGED_IN = !!CFG.customerLoggedIn;

  var designCounter = 1; // starts at 1 because design #1 is already in the DOM

  // ─── Helpers ──────────────────────────────────────────────────────────────

  function show(el) { if (el) el.hidden = false; }
  function hide(el) { if (el) el.hidden = true; }

  // ─── Design block template ────────────────────────────────────────────────

  function createDesignBlock(index) {
    var block = document.createElement('div');
    block.className = 'vec-design-block';
    block.setAttribute('data-design-index', index);

    block.innerHTML =
      '<div class="vec-design-header">' +
        '<h3 class="dtf-section-title">Design #' + (index + 1) + '</h3>' +
        '<button type="button" class="vec-remove-design" aria-label="Remove design">&#10005; Remove</button>' +
      '</div>' +

      '<div class="dtf-section">' +
        '<div class="dtf-field">' +
          '<label class="dtf-label">' +
            'Upload your artwork file ' +
            '<span class="dtf-required" aria-hidden="true">*</span>' +
          '</label>' +
          '<div class="dtf-upload-area vec-upload-area">' +
            '<input type="file" class="dtf-file-input vec-file-input" accept="*" aria-required="true">' +
            '<div class="dtf-upload-placeholder vec-upload-placeholder">' +
              '<span class="dtf-upload-icon" aria-hidden="true">&#8679;</span>' +
              '<span class="dtf-upload-cta">Click to upload or drag &amp; drop</span>' +
              '<span class="dtf-upload-formats">PNG &middot; JPG &middot; PDF &middot; AI &middot; PSD &middot; EPS &middot; SVG</span>' +
            '</div>' +
            '<div class="dtf-upload-selected vec-upload-selected" hidden>' +
              '<img class="dtf-preview-img vec-preview-img" src="" alt="" hidden title="Click to preview" style="cursor:pointer;">' +
              '<div class="dtf-preview-fallback vec-preview-fallback" hidden aria-hidden="true"><span class="dtf-preview-fallback__icon">&#128196;</span></div>' +
              '<span class="dtf-upload-filename vec-upload-filename"></span>' +
              '<button type="button" class="dtf-remove-file vec-remove-file" aria-label="Remove file">&#10005; Remove</button>' +
            '</div>' +
          '</div>' +
          '<div class="dtf-upload-progress vec-upload-progress" hidden>' +
            '<div class="dtf-progress-track">' +
              '<div class="dtf-progress-fill vec-progress-fill" style="width:0%"></div>' +
            '</div>' +
            '<span class="dtf-upload-status vec-upload-status">Uploading&hellip;</span>' +
          '</div>' +
          '<span class="dtf-error vec-file-error" role="alert"></span>' +
        '</div>' +
      '</div>' +

      '<div class="dtf-section">' +
        '<div class="dtf-field">' +
          '<label class="dtf-label">Description / Notes</label>' +
          '<textarea class="dtf-textarea vec-notes" rows="3" placeholder="Describe what you need for this design&hellip;"></textarea>' +
        '</div>' +
      '</div>';

    return block;
  }

  // ─── File upload (Supabase) ───────────────────────────────────────────────

  function uploadFileToSupabase(file, block, callback) {
    var progress = block.querySelector('.vec-upload-progress');
    var fill     = block.querySelector('.vec-progress-fill');
    var status   = block.querySelector('.vec-upload-status');

    show(progress);
    if (fill) fill.style.width = '0%';
    if (status) status.textContent = 'Uploading\u2026';

    var ext        = file.name.split('.').pop();
    var uniqueName = Date.now() + '-' + Math.random().toString(36).slice(2) + '.' + ext;
    var encoded    = encodeURIComponent(SUPABASE_BUCKET);
    var uploadUrl  = SUPABASE_URL + '/storage/v1/object/' + encoded + '/' + uniqueName;
    var publicUrl  = SUPABASE_URL + '/storage/v1/object/public/' + encoded + '/' + uniqueName;

    var xhr = new XMLHttpRequest();
    xhr.open('POST', uploadUrl, true);
    xhr.setRequestHeader('Authorization', 'Bearer ' + SUPABASE_KEY);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

    xhr.upload.onprogress = function (e) {
      if (e.lengthComputable && fill) {
        fill.style.width = Math.round((e.loaded / e.total) * 100) + '%';
      }
    };

    xhr.onload = function () {
      hide(progress);
      if (xhr.status >= 200 && xhr.status < 300) {
        callback(publicUrl);
      } else {
        callback(null);
        var errEl = block.querySelector('.vec-file-error');
        if (errEl) { errEl.textContent = 'Upload failed. Please try again.'; errEl.hidden = false; }
      }
    };

    xhr.onerror = function () {
      hide(progress);
      callback(null);
    };

    xhr.send(file);
  }

  // ─── Wire up a design block (file input, remove, drag-drop) ───────────────

  function initDesignBlock(block) {
    var fileInput   = block.querySelector('.vec-file-input');
    var removeFile  = block.querySelector('.vec-remove-file');
    var removeBlock = block.querySelector('.vec-remove-design');
    var uploadArea  = block.querySelector('.vec-upload-area');

    // Store uploaded URL on the block element
    block._uploadedUrl = '';
    block._fileName = '';

    function handleFile(file) {
      if (!file) return;
      var MAX_BYTES = 200 * 1024 * 1024;
      var errEl = block.querySelector('.vec-file-error');
      if (file.size > MAX_BYTES) {
        if (errEl) { errEl.textContent = 'File is too large (max 200 MB).'; errEl.hidden = false; }
        return;
      }
      if (errEl) { errEl.textContent = ''; errEl.hidden = true; }

      hide(block.querySelector('.vec-upload-placeholder'));
      show(block.querySelector('.vec-upload-selected'));
      var fnEl = block.querySelector('.vec-upload-filename');
      if (fnEl) fnEl.textContent = file.name;
      block._fileName = file.name;

      // Show image preview (like DTF form)
      var previewImg = block.querySelector('.vec-preview-img');
      var previewFallback = block.querySelector('.vec-preview-fallback');
      if (previewImg) {
        hide(previewImg);
        if (previewFallback) hide(previewFallback);

        var objectUrl = URL.createObjectURL(file);
        previewImg.onload = function () {
          show(previewImg);
          if (previewFallback) hide(previewFallback);
        };
        previewImg.onerror = function () {
          previewImg.src = '';
          hide(previewImg);
          URL.revokeObjectURL(objectUrl);
          if (previewFallback) show(previewFallback);
        };
        previewImg.src = objectUrl;
        block._objectUrl = objectUrl;
      }

      if (SUPABASE_URL && SUPABASE_KEY) {
        uploadFileToSupabase(file, block, function (url) {
          block._uploadedUrl = url || '[FILE] ' + file.name;
        });
      } else {
        block._uploadedUrl = '[FILE] ' + file.name;
      }
    }

    if (fileInput) {
      fileInput.addEventListener('change', function () {
        handleFile(fileInput.files[0] || null);
      });
    }

    if (removeFile) {
      removeFile.addEventListener('click', function (e) {
        e.stopPropagation();
        if (fileInput) fileInput.value = '';
        block._uploadedUrl = '';
        block._fileName = '';
        show(block.querySelector('.vec-upload-placeholder'));
        hide(block.querySelector('.vec-upload-selected'));
        // Clear preview
        var previewImg = block.querySelector('.vec-preview-img');
        if (previewImg) { previewImg.src = ''; hide(previewImg); }
        if (block._objectUrl) { URL.revokeObjectURL(block._objectUrl); block._objectUrl = null; }
        var previewFallback = block.querySelector('.vec-preview-fallback');
        if (previewFallback) hide(previewFallback);
        var errEl = block.querySelector('.vec-file-error');
        if (errEl) { errEl.textContent = ''; errEl.hidden = true; }
      });
    }

    if (removeBlock) {
      removeBlock.addEventListener('click', function () {
        block.remove();
        renumberDesigns();
      });
    }

    // Drag and drop
    if (uploadArea && fileInput) {
      uploadArea.addEventListener('dragover', function (e) {
        e.preventDefault();
        uploadArea.classList.add('dtf-upload-area--dragover');
      });
      uploadArea.addEventListener('dragleave', function () {
        uploadArea.classList.remove('dtf-upload-area--dragover');
      });
      uploadArea.addEventListener('drop', function (e) {
        e.preventDefault();
        uploadArea.classList.remove('dtf-upload-area--dragover');
        if (e.dataTransfer.files.length > 1) {
          alert('You can only upload 1 image at a time. Please fill out a separate form for each design.');
          return;
        }
        var file = e.dataTransfer.files[0];
        if (file) {
          try {
            var dt = new DataTransfer();
            dt.items.add(file);
            fileInput.files = dt.files;
          } catch (err) {}
          handleFile(file);
        }
      });
    }
  }

  function renumberDesigns() {
    var blocks = document.querySelectorAll('.vec-design-block');
    blocks.forEach(function (block, i) {
      block.setAttribute('data-design-index', i);
      var title = block.querySelector('.dtf-section-title');
      if (title) title.textContent = 'Design #' + (i + 1);
    });
    designCounter = blocks.length;
  }

  // ─── Collect all design data ──────────────────────────────────────────────

  function collectDesigns() {
    var blocks = document.querySelectorAll('.vec-design-block');
    var designs = [];
    blocks.forEach(function (block, i) {
      var notes = block.querySelector('.vec-notes');
      designs.push({
        design_number: i + 1,
        file_url: block._uploadedUrl || '',
        file_name: block._fileName || '',
        description: notes ? notes.value.trim() : ''
      });
    });
    return designs;
  }

  // ─── Validation ───────────────────────────────────────────────────────────

  function validateDesigns() {
    var blocks = document.querySelectorAll('.vec-design-block');
    var valid = true;
    var errorsBox = document.getElementById('vec-submit-errors');

    blocks.forEach(function (block) {
      var errEl = block.querySelector('.vec-file-error');
      // Must have uploaded a file
      if (!block._uploadedUrl) {
        if (errEl) { errEl.textContent = 'Please upload your artwork file.'; errEl.hidden = false; }
        valid = false;
      } else {
        if (errEl) { errEl.textContent = ''; errEl.hidden = true; }
      }
    });

    if (!valid && errorsBox) {
      errorsBox.textContent = 'Please upload artwork for all designs before requesting a quote.';
      show(errorsBox);
    } else if (errorsBox) {
      hide(errorsBox);
    }

    return valid;
  }

  // ─── POST to Zapier webhook ───────────────────────────────────────────────

  function submitQuote(firstName, lastName, email, designs) {
    var payload = {
      product: PRODUCT_TITLE,
      customer_first_name: firstName,
      customer_last_name: lastName,
      customer_email: email,
      submitted_at: new Date().toISOString(),
      designs: designs
    };

    return fetch(WEBHOOK_URL, {
      method: 'POST',
      mode: 'no-cors',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
  }

  // ─── Dialog helpers ───────────────────────────────────────────────────────

  function openDialog(dialog) {
    if (dialog.showModal) {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
  }

  function closeDialog(dialog) {
    if (dialog.close) {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
    }
  }

  // ─── Reset form (clear all designs back to one empty block) ────────────────

  function resetForm() {
    var container = document.getElementById('vec-designs-container');
    if (!container) return;

    // Remove all design blocks except the first
    var blocks = container.querySelectorAll('.vec-design-block');
    blocks.forEach(function (block, i) {
      if (i > 0) block.remove();
    });

    // Reset the first block
    var first = container.querySelector('.vec-design-block');
    if (first) {
      var fileInput = first.querySelector('.vec-file-input');
      if (fileInput) fileInput.value = '';
      first._uploadedUrl = '';
      first._fileName = '';
      show(first.querySelector('.vec-upload-placeholder'));
      hide(first.querySelector('.vec-upload-selected'));
      var previewImg = first.querySelector('.vec-preview-img');
      if (previewImg) { previewImg.src = ''; hide(previewImg); }
      if (first._objectUrl) { URL.revokeObjectURL(first._objectUrl); first._objectUrl = null; }
      var previewFallback = first.querySelector('.vec-preview-fallback');
      if (previewFallback) hide(previewFallback);
      var notes = first.querySelector('.vec-notes');
      if (notes) notes.value = '';
      var errEl = first.querySelector('.vec-file-error');
      if (errEl) { errEl.textContent = ''; errEl.hidden = true; }
      var title = first.querySelector('.dtf-section-title');
      if (title) title.textContent = 'Design #1';
      first.setAttribute('data-design-index', '0');
    }

    designCounter = 1;

    // Clear submit errors
    var errorsBox = document.getElementById('vec-submit-errors');
    if (errorsBox) { errorsBox.textContent = ''; hide(errorsBox); }
  }

  // ─── Init ─────────────────────────────────────────────────────────────────

  function init() {
    var container   = document.getElementById('vec-designs-container');
    var addBtn      = document.getElementById('vec-add-design');
    var quoteBtn    = document.getElementById('vec-get-quote-btn');
    var dialog      = document.getElementById('vec-quote-dialog');
    var dialogClose = document.getElementById('vec-quote-dialog-close');
    var submitBtn   = document.getElementById('vec-quote-submit-btn');
    var thankyou    = document.getElementById('vec-quote-thankyou');
    var contactForm = document.getElementById('vec-quote-contact');
    var thankyouClose = document.getElementById('vec-thankyou-close');

    if (!container) return;

    // Init the first design block already in the DOM
    var firstBlock = container.querySelector('.vec-design-block');
    if (firstBlock) initDesignBlock(firstBlock);

    // Add Design button
    if (addBtn) {
      addBtn.addEventListener('click', function () {
        var newBlock = createDesignBlock(designCounter);
        container.appendChild(newBlock);
        initDesignBlock(newBlock);
        designCounter++;
        newBlock.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }

    // Get Custom Quote button — validate then open dialog
    if (quoteBtn && dialog) {
      quoteBtn.addEventListener('click', function () {
        if (!validateDesigns()) {
          var firstErr = document.querySelector('.vec-file-error:not([hidden])');
          if (firstErr) firstErr.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }

        // If logged in, submit immediately and show thank-you
        if (CUSTOMER_LOGGED_IN) {
          var designs = collectDesigns();
          var firstName = CFG.customerFirstName;
          var lastName = CFG.customerLastName;
          var email = CFG.customerEmail;

          openDialog(dialog);

          submitQuote(firstName, lastName, email, designs)
            .then(function () {
              show(thankyou);
              hide(contactForm);
              resetForm();
            })
            .catch(function () {
              // Still show thank you — webhook is fire-and-forget
              show(thankyou);
              hide(contactForm);
              resetForm();
            });
        } else {
          // Show contact form
          show(contactForm);
          hide(thankyou);
          openDialog(dialog);
        }
      });
    }

    // Dialog close button
    if (dialogClose && dialog) {
      dialogClose.addEventListener('click', function () { closeDialog(dialog); });
    }
    if (dialog) {
      dialog.addEventListener('click', function (e) { if (e.target === dialog) closeDialog(dialog); });
      dialog.addEventListener('cancel', function () { closeDialog(dialog); });
    }

    // Thank-you close button
    if (thankyouClose && dialog) {
      thankyouClose.addEventListener('click', function () { closeDialog(dialog); });
    }

    // Submit quote (non-logged-in flow)
    if (submitBtn) {
      submitBtn.addEventListener('click', function () {
        var firstNameInput = document.getElementById('vec-quote-first-name');
        var lastNameInput  = document.getElementById('vec-quote-last-name');
        var emailInput     = document.getElementById('vec-quote-email');
        var errorsBox      = document.getElementById('vec-quote-errors');
        var label          = document.getElementById('vec-quote-submit-label');
        var loading        = document.getElementById('vec-quote-submit-loading');

        var firstName = firstNameInput ? firstNameInput.value.trim() : '';
        var lastName  = lastNameInput  ? lastNameInput.value.trim()  : '';
        var email     = emailInput     ? emailInput.value.trim()     : '';

        // Validate
        if (!firstName || !lastName || !email) {
          if (errorsBox) {
            errorsBox.textContent = 'Please enter your first name, last name, and email.';
            show(errorsBox);
          }
          return;
        }

        // Basic email check
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          if (errorsBox) {
            errorsBox.textContent = 'Please enter a valid email address.';
            show(errorsBox);
          }
          return;
        }

        hide(errorsBox);
        submitBtn.disabled = true;
        hide(label);
        show(loading);

        var designs = collectDesigns();

        submitQuote(firstName, lastName, email, designs)
          .then(function () {
            submitBtn.disabled = false;
            show(label);
            hide(loading);
            hide(contactForm);
            show(thankyou);
            resetForm();
          })
          .catch(function () {
            submitBtn.disabled = false;
            show(label);
            hide(loading);
            // Still show thank you — webhook is fire-and-forget
            hide(contactForm);
            show(thankyou);
            resetForm();
          });
      });
    }

    // Init tooltips
    document.querySelectorAll('#vec-form-wrapper .dtf-tooltip').forEach(function (tip) {
      tip.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          tip.classList.toggle('dtf-tooltip--open');
        }
        if (e.key === 'Escape') {
          tip.classList.remove('dtf-tooltip--open');
        }
      });
      tip.addEventListener('blur', function () {
        tip.classList.remove('dtf-tooltip--open');
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();